import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import _ from "lodash";
import i18next from "i18next";
import {
  AttributeType,
  DocumentTypePropertyType,
  FieldType,
  Operator,
  REGEXP_DECIMAL,
  REGEXP_INTEGER,
  REGEXP_PERCENTAGE,
  ThirdpartyScoreType
} from "../../../../../services/common/Constants";
import RuleSelectInput from "../../../../../components/rules/RuleSelectInput";
import RuleStandardInput from "../../../../../components/rules/RuleStandardInput";
import RuleMultipleValueInput from "../../../../../components/rules/RuleMultipleValueInput";
import EAMultipleSelectInput from "../../../../../components/rules/EAMultipleSelectInput";
import EAInputBloc from "../../../../../components/common/EAInputBloc";
import EADatePicker from "../../../../../components/common/EADatePicker";
import { CalculationResultType } from "../../../../../services/common/CalculatorUtils";
import EAAutoCompleteInput from "components/inputs/EAAutoCompleteInput";

const getCriterionValuesForNumeric = (formMethods, selectedOperator, fieldName, showLabel, readOnly) => {
  const {
    setError,
    clearErrors,
  } = formMethods;
  switch (selectedOperator) {
    case Operator.EQUAL:
    case Operator.NOT_EQUAL:
    case Operator.GREATER_THAN:
    case Operator.GREATER_OR_EQUAL:
    case Operator.LESS_THAN:
    case Operator.LESS_OR_EQUAL:
      return (
        <RuleStandardInput
          fieldName={`${fieldName}.${0}`}
          isRequired={true}
          label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
          placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
          validate={() => {
            return null;
          }}
          disabled={readOnly}
        />
      );

    case Operator.BETWEEN:
      return (
        <>
          {/*TODO*/}
        </>
      );

    case Operator.IS_NULL:
    case Operator.NOT_NULL:
      return (<></>);

    case Operator.IN_SET:
    case Operator.NOT_IN_SET:
      return (
        <RuleMultipleValueInput
          fieldName={`${fieldName}`}
          label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
          placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
          isRequired
          validate={(value) => {
            if (value.length === 0) {
              setError(`${fieldName}`, { type: 'manual', message: i18next.t(`rules.configuration.criteria.fields.value.errors.isEmpty`) });
              return false;
            } else {
              clearErrors(`${fieldName}`);
              return true;
            }
          }}
          disabled={readOnly}
        />
      );

    default:
      return (<></>);

  }
}

const CriterionValues = ({ 
  availableValueOptions, 
  fieldName, 
  showLabel = false,
  selectedFieldType,
  selectedFieldSubType,
  selectedOperator,
  variantAutoComplete,
  readOnly = false
 }) => {
  const formMethods = useFormContext();
  const {
    setValue,
    setError,
    clearErrors,
  } = formMethods;

  switch (selectedFieldType) {
    case FieldType.TEXT:
      return (
        <>
          {(() => {
            switch (selectedOperator) {
              case Operator.EQUAL:
              case Operator.NOT_EQUAL:
                return !_.isEmpty(availableValueOptions) ? (
                  variantAutoComplete === true ?
                    <EAInputBloc
                      label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`} customLabelStyle={{ fontSize: "14px" }}>
                      <EAAutoCompleteInput
                        fieldName={`${fieldName}.${0}`}
                        options={availableValueOptions}
                        placeholder={i18next.t(`rules.configuration.criteria.fields.value.autocompletePlaceholder`)}
                        isRequired
                        multiple={false}
                        disabled={readOnly}
                      />
                    </EAInputBloc>
                    :
                    <RuleSelectInput
                      fieldName={`${fieldName}.${0}`}
                      label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                      placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                      options={availableValueOptions}
                      isRequired
                      validate={() => {
                        return null;
                      }}
                      labelVariant="chip"
                      disabled={readOnly}
                    />
                ) : (
                  <RuleStandardInput
                    fieldName={`${fieldName}.${0}`}
                    isRequired={true}
                    label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                    placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                    validate={() => {
                      /*TODO: custom validation if any*/
                      return null;
                    }}
                    disabled={readOnly}
                  />
                );

              case Operator.CONTAINS:
              case Operator.NOT_CONTAINS:
              case Operator.STARTS_WITH:
              case Operator.NOT_STARTS_WITH:
              case Operator.ENDS_WITH:
              case Operator.NOT_ENDS_WITH:
                return (
                  <RuleStandardInput
                    fieldName={`${fieldName}.${0}`}
                    isRequired={true}
                    label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                    placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                    validate={() => {
                      /*TODO: custom validation if any*/
                      return null;
                    }}
                    disabled={readOnly}
                  />
                );

              case Operator.IS_NULL:
              case Operator.NOT_NULL:
                return (<></>);

              case Operator.IN_SET:
              case Operator.NOT_IN_SET:
                return !_.isEmpty(availableValueOptions) ? (
                  variantAutoComplete === true ?
                  <EAInputBloc label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`} customLabelStyle={{ fontSize: "14px" }}>
                    <EAAutoCompleteInput
                      fieldName={`${fieldName}`}
                      options={availableValueOptions}
                      placeholder={i18next.t(`rules.configuration.criteria.fields.value.autocompletePlaceholder`)}
                      isRequired
                      multiple={true}
                      disabled={readOnly}
                    />
                  </EAInputBloc>
                  :
                  <EAInputBloc label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`} 
                      customLabelStyle={{ fontSize: "14px" }} customChildStyle={{ marginTop: "0px" }}>
                    <EAMultipleSelectInput
                      fieldName={`${fieldName}`}
                      options={availableValueOptions}
                      isRequired
                      onChange={(e) => {
                        setValue(`${fieldName}`, e.target.value);
                      }}
                      placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                      validate={(value) => {
                        if (value.length === 0) {
                          setError(`${fieldName}`, { type: 'manual', message: i18next.t(`rules.configuration.criteria.fields.value.errors.isEmpty`) });
                          return false;
                        } else {
                          clearErrors(`${fieldName}`);
                          return true;
                        }
                      }}
                      disabled={readOnly}
                    />
                  </EAInputBloc>
                ) : (
                  <RuleMultipleValueInput
                    fieldName={`${fieldName}`}
                    label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                    placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                    isRequired
                    validate={(value) => {
                      if (value.length === 0) {
                        setError(`${fieldName}`, { type: 'manual', message: i18next.t(`rules.configuration.criteria.fields.value.errors.isEmpty`) });
                        return false;
                      } else {
                        clearErrors(`${fieldName}`);
                        return true;
                      }
                    }}
                    disabled={readOnly}
                  />
                );

              default:
                return (<></>);
            }
          })()}
        </>
      );

    case FieldType.NUMERIC:
      return (
        <>
          {(() => {
            return getCriterionValuesForNumeric(formMethods, selectedOperator, fieldName, showLabel, readOnly);
          })()}
        </>
      );

    case FieldType.DATE:
      return (
        <>
          {(() => {
            switch (selectedOperator) {
              case Operator.EQUAL:
              case Operator.NOT_EQUAL:
              case Operator.GREATER_THAN:
              case Operator.GREATER_OR_EQUAL:
              case Operator.LESS_THAN:
              case Operator.LESS_OR_EQUAL:
                return (
                  <EAInputBloc label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`} customLabelStyle={{ fontSize: "14px" }}>
                    <Controller
                      control={formMethods.control}
                      name={`${fieldName}.${0}`}
                      rules={{
                        required: i18next.t(`infos.required`),
                        validate: (value) => {
                          if (!value) {
                            return i18next.t(`infos.required`);
                          }
                          // Add more custom validation logic if needed
                          return true;
                        }
                      }}
                      render={({ field }) => (
                        <EADatePicker
                          name={field.name}
                          formMethods={formMethods}
                          disableHighlightToday
                          isRequired
                          disabled={readOnly}
                        />
                      )}
                    />
                  </EAInputBloc>
                );

              case Operator.BETWEEN:
                return (
                  <>
                    {/*TODO*/}
                  </>
                );

              case Operator.IS_NULL:
              case Operator.NOT_NULL:
                return (<></>);

              case Operator.IN_SET:
              case Operator.NOT_IN_SET:
                return (
                  <>
                    {/*TODO*/}
                  </>
                );

              default:
                return (<></>);
            }
          })()}
        </>
      );

    case FieldType.BOOLEAN:
    case FieldType.CUSTOM:
    case FieldType.RISK:
    case FieldType.ACTIVITY:
      return (
        <>
          {(() => {
            if (selectedFieldSubType === ThirdpartyScoreType.CREDITSAFE_NUMERIC) {
              return getCriterionValuesForNumeric(formMethods, selectedOperator, fieldName, showLabel, readOnly);
            } else {
            const autoCompleteInputToUse = variantAutoComplete
            switch (selectedOperator) {
              case Operator.EQUAL:
              case Operator.NOT_EQUAL:
                if (autoCompleteInputToUse) {
                  return (
                    <EAInputBloc label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`} 
                      customLabelStyle={{ fontSize: "14px" }} customChildStyle={{marginTop: "0"}}>
                      <EAAutoCompleteInput
                        fieldName={`${fieldName}.${0}`}
                        options={availableValueOptions}
                        placeholder={i18next.t(`rules.configuration.criteria.fields.value.autocompletePlaceholder`)}
                        isRequired
                        multiple={false}
                        disabled={readOnly}
                      />
                    </EAInputBloc>
                  );
                } else {
                  return (
                    <RuleSelectInput
                      fieldName={`${fieldName}.${0}`}
                      label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                      placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                      options={availableValueOptions}
                      isRequired
                      validate={() => {
                        /*TODO: custom validation if any*/
                        return null;
                      }}
                      labelVariant='chip'
                      disabled={readOnly}
                    />
                  );
                }

              case Operator.IN_SET:
              case Operator.NOT_IN_SET:
                if (autoCompleteInputToUse) {
                  return (
                    <EAInputBloc label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`} customLabelStyle={{ fontSize: "14px" }}>
                      <EAAutoCompleteInput
                        fieldName={`${fieldName}`}
                        options={availableValueOptions}
                        placeholder={i18next.t(`rules.configuration.criteria.fields.value.autocompletePlaceholder`)}
                        isRequired
                        multiple={true}
                        disabled={readOnly}
                      />
                    </EAInputBloc>
                  );
                } else {
                  return (
                    <EAInputBloc label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`} 
                        customLabelStyle={{ fontSize: "14px" }} customChildStyle={{ marginTop: "0px" }}>
                      <EAMultipleSelectInput
                        fieldName={`${fieldName}`}
                        options={availableValueOptions}
                        isRequired
                        onChange={(e) => {
                          setValue(`${fieldName}`, e.target.value);
                        }}
                        placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                        disabled={readOnly}
                      />
                    </EAInputBloc>
                  );
                }

                case Operator.GREATER_THAN:
                case Operator.GREATER_OR_EQUAL:
                case Operator.LESS_THAN:
                case Operator.LESS_OR_EQUAL:
                  return (
                    <RuleSelectInput
                      fieldName={`${fieldName}.${0}`}
                      label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                      placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                      options={availableValueOptions}
                      isRequired
                      validate={() => {
                        /*TODO: custom validation if any*/
                        return null;
                      }}
                      labelVariant='chip'
                      disabled={readOnly}
                    />
                  );


              default:
                return (<></>);
            }
          }
          })()}
        </>
      );

    case FieldType.ATTRIBUTE:
    case FieldType.CONTEXT_RANK:
      return (
        <>
          {(() => {
            switch (selectedFieldSubType) {
              case AttributeType.LIST_UNIQUE:
                return (
                  <>
                    {(() => {
                      switch (selectedOperator) {
                        case Operator.EQUAL:
                        case Operator.NOT_EQUAL:
                          return (
                            <RuleSelectInput
                              fieldName={`${fieldName}.${0}`}
                              label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                              placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                              options={availableValueOptions}
                              isRequired
                              validate={() => {
                                /*TODO: custom validation if any*/
                                return null;
                              }}
                              labelVariant='chip'
                              disabled={readOnly}
                            />
                          );

                        case Operator.CONTAINS:
                        case Operator.NOT_CONTAINS:
                        case Operator.STARTS_WITH:
                        case Operator.NOT_STARTS_WITH:
                        case Operator.ENDS_WITH:
                        case Operator.NOT_ENDS_WITH:
                          return (
                            <RuleStandardInput
                              fieldName={`${fieldName}.${0}`}
                              label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                              placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                              isRequired
                              validate={() => {
                                /*TODO: custom validation if any*/
                                return null;
                              }}
                              disabled={readOnly}
                            />
                          );

                        case Operator.IS_NULL:
                        case Operator.NOT_NULL:
                          return (
                            <>
                              {/*MEANS ATTRIBUTE IS UNDEFINED*/}
                            </>
                          );

                        case Operator.IN_SET:
                        case Operator.NOT_IN_SET:
                          return (
                            <EAInputBloc label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`} 
                                customLabelStyle={{ fontSize: "14px" }} customChildStyle={{ marginTop: "0px" }}>
                              <EAMultipleSelectInput
                                fieldName={`${fieldName}`}
                                options={availableValueOptions}
                                isRequired
                                onChange={(e) => {
                                  setValue(`${fieldName}`, e.target.value);
                                }}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                disabled={readOnly}
                              />
                            </EAInputBloc>
                          );

                        default:
                          return (<></>);
                      }
                    })()}
                  </>
                );

              case AttributeType.LIST_MULTIPLE:
                return (
                  <>
                    {(() => {
                      switch (selectedOperator) {
                        case Operator.EQUAL:
                          return (
                            <EAInputBloc label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`} 
                                customLabelStyle={{ fontSize: "14px" }} customChildStyle={{ marginTop: "0px" }}>
                              <EAMultipleSelectInput
                                fieldName={`${fieldName}`}
                                options={availableValueOptions}
                                isRequired
                                onChange={(e) => {
                                  setValue(`${fieldName}`, e.target.value);
                                }}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                disabled={readOnly}
                              />
                            </EAInputBloc>
                          );

                        case Operator.CONTAINS:
                        case Operator.NOT_CONTAINS:
                          return (
                            <EAInputBloc label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`} 
                                customLabelStyle={{ fontSize: "14px" }} customChildStyle={{ marginTop: "0px" }}>
                              <EAMultipleSelectInput
                                fieldName={`${fieldName}`}
                                options={availableValueOptions}
                                isRequired
                                onChange={(e) => {
                                  setValue(`${fieldName}`, e.target.value);
                                }}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                disabled={readOnly}
                              />
                            </EAInputBloc>
                          );

                        case Operator.IS_NULL:
                        case Operator.NOT_NULL:
                          return (
                            <>
                              {/*MEANS ATTRIBUTE IS UNDEFINED*/}
                            </>
                          );

                        default:
                          return (<></>);
                      }
                    })()}
                  </>
                );

              case AttributeType.INTEGER:
              case AttributeType.DECIMAL:
              case AttributeType.PERCENTAGE:
                return (
                  <>
                    {
                      (() => {
                        switch (selectedOperator) {
                          case Operator.EQUAL:
                          case Operator.NOT_EQUAL:
                          case Operator.GREATER_THAN:
                          case Operator.GREATER_OR_EQUAL:
                          case Operator.LESS_THAN:
                          case Operator.LESS_OR_EQUAL:
                            return (
                              <RuleStandardInput
                                fieldName={`${fieldName}.${0}`}
                                label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                isRequired
                                validate={(value) => {
                                  switch (selectedFieldSubType) {
                                    case AttributeType.INTEGER:
                                      return value && !value.match(REGEXP_INTEGER)
                                        ? i18next.t("attributes.errors.integer")
                                        : null;
                                    case AttributeType.DECIMAL:
                                      return value && !value.match(REGEXP_DECIMAL)
                                        ? i18next.t("attributes.errors.decimal")
                                        : null;
                                    case AttributeType.PERCENTAGE:
                                      return value && !value.match(REGEXP_PERCENTAGE)
                                        ? i18next.t("attributes.errors.percentage")
                                        : null;
                                    default:
                                      return null;
                                  }
                                }}
                                disabled={readOnly}
                              />
                            );

                          case Operator.BETWEEN:
                            return (
                              <>
                                {/*TODO: Two values*/}
                              </>
                            );

                          case Operator.IS_NULL:
                          case Operator.NOT_NULL:
                            return (
                              <>
                                {/*MEANS ATTRIBUTE IS UNDEFINED*/}
                              </>
                            );

                          case Operator.IN_SET:
                          case Operator.NOT_IN_SET:
                            return (
                              <RuleMultipleValueInput
                                fieldName={`${fieldName}`}
                                label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                isRequired
                                validate={(value) => {
                                  let isValid = true;
                                  //TODO: uncomment this when validation issue fixed
                                  switch (selectedFieldSubType) {
                                    case AttributeType.INTEGER:
                                      if (value && !value.match(REGEXP_INTEGER)) {
                                        setError(`${fieldName}`, {
                                          type: 'manual',
                                          message: i18next.t("attributes.errors.integer")
                                        });

                                        isValid = false;
                                      }
                                      break;

                                    case AttributeType.DECIMAL:
                                      if (value && !value.match(REGEXP_DECIMAL)) {
                                        setError(`${fieldName}`, {
                                          type: 'manual',
                                          message: i18next.t("attributes.errors.decimal")
                                        });

                                        isValid = false;
                                      }
                                      break;

                                    case AttributeType.PERCENTAGE:
                                      if (value && !value.match(REGEXP_PERCENTAGE)) {
                                        setError(`${fieldName}`, {
                                          type: 'manual',
                                          message: i18next.t("attributes.errors.percentage")
                                        });

                                        isValid = false;
                                      }
                                      break;

                                    default:
                                      break;
                                  }

                                  return isValid;
                                }}
                                disabled={readOnly}
                              />
                            );

                          default:
                            return (<></>);
                        }
                      })()
                    }
                  </>
                );

              case AttributeType.BOOLEAN:
                return (
                  <>
                    {
                      (() => {
                        switch (selectedOperator) {
                          case Operator.EQUAL:
                          case Operator.NOT_EQUAL:
                            return (
                              <RuleSelectInput
                                fieldName={`${fieldName}.${0}`}
                                label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                options={availableValueOptions}
                                isRequired
                                validate={() => {
                                  /*TODO: custom validation if any*/
                                  return null;
                                }}
                                labelVariant='chip'
                                disabled={readOnly}
                              />
                            );

                          case Operator.IS_NULL:
                          case Operator.NOT_NULL:
                            return (
                              <>
                                {/*MEANS ATTRIBUTE IS UNDEFINED*/}
                              </>
                            );

                          default:
                            return (<></>);
                        }
                      })()
                    }
                  </>
                );

              case AttributeType.DATE:
                return (
                  <>
                    {(() => {
                      switch (selectedOperator) {
                        case Operator.EQUAL:
                        case Operator.NOT_EQUAL:
                        case Operator.GREATER_THAN:
                        case Operator.GREATER_OR_EQUAL:
                        case Operator.LESS_THAN:
                        case Operator.LESS_OR_EQUAL:
                          return (
                            <EAInputBloc label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`} customLabelStyle={{ fontSize: "14px" }}>
                              <Controller
                                control={formMethods.control}
                                name={`${fieldName}.${0}`}
                                rules={{
                                  required: i18next.t(`infos.required`),
                                  validate: (value) => {
                                    if (!value) {
                                      return i18next.t(`infos.required`);
                                    }
                                    // Add more custom validation logic if needed
                                    return true;
                                  }
                                }}
                                render={({ field }) => (
                                  <EADatePicker
                                    name={field.name}
                                    formMethods={formMethods}
                                    disableHighlightToday
                                    isRequired
                                    disabled={readOnly}
                                  />
                                )}
                              />
                            </EAInputBloc>
                          );


                        case Operator.BETWEEN:
                          return (
                            <>
                              {/*TODO*/}
                            </>
                          );

                        case Operator.IS_NULL:
                        case Operator.NOT_NULL:
                          return (<></>);

                        case Operator.IN_SET:
                        case Operator.NOT_IN_SET:
                          return (
                            <>
                              {/*TODO*/}
                            </>
                          );

                        default:
                          return (<></>);
                      }
                    })()}
                  </>
                );

              default:
                break;
            }
          })()}
        </>
      );

    case FieldType.CALCULATED_ATTRIBUTE:
    case FieldType.INDICATOR:
      return (
        <>
          {(() => {
            switch (selectedFieldSubType) {
              case CalculationResultType.STRING:
                return (
                  <>
                    {(() => {
                      switch (selectedOperator) {
                        case Operator.EQUAL:
                        case Operator.NOT_EQUAL:
                          return !_.isEmpty(availableValueOptions) ? (
                            <RuleSelectInput
                              fieldName={`${fieldName}.${0}`}
                              label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                              placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                              options={availableValueOptions}
                              isRequired
                              validate={() => {
                                return null;
                              }}
                              labelVariant="chip"
                              disabled={readOnly}
                            />
                          ) : (
                            <RuleStandardInput
                              fieldName={`${fieldName}.${0}`}
                              isRequired
                              label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                              placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                              validate={() => {
                                return null;
                              }}
                              disabled={readOnly}
                            />
                          );

                        case Operator.CONTAINS:
                        case Operator.NOT_CONTAINS:
                        case Operator.STARTS_WITH:
                        case Operator.NOT_STARTS_WITH:
                        case Operator.ENDS_WITH:
                        case Operator.NOT_ENDS_WITH:
                          return (
                            <RuleStandardInput
                              fieldName={`${fieldName}.${0}`}
                              isRequired
                              label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                              placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                              validate={() => {
                                return null;
                              }}
                              disabled={readOnly}
                            />
                          );

                        case Operator.IS_NULL:
                        case Operator.NOT_NULL:
                          return (<></>);

                        case Operator.IN_SET:
                        case Operator.NOT_IN_SET:
                          return !_.isEmpty(availableValueOptions) ? (
                            <EAInputBloc label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`} 
                                customLabelStyle={{ fontSize: "14px" }} customChildStyle={{ marginTop: "0px" }}>
                              <EAMultipleSelectInput
                                fieldName={`${fieldName}`}
                                options={availableValueOptions}
                                isRequired
                                variant="ruleInput"
                                onChange={(e) => {
                                  setValue(`${fieldName}`, e.target.value);
                                }}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                validate={(value) => {
                                  if (value.length === 0) {
                                    setError(`${fieldName}`, {
                                      type: 'manual',
                                      message: i18next.t(`infos.required`)
                                    });
                                    return false;
                                  } else {
                                    clearErrors(`${fieldName}`);
                                    return true;
                                  }
                                }}
                                disabled={readOnly}
                              />
                            </EAInputBloc>
                          ) : (
                            <RuleMultipleValueInput
                              fieldName={`${fieldName}`}
                              isRequired
                              label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                              placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                              validate={(value) => {
                                if (value.length === 0) {
                                  setError(`${fieldName}`, {
                                    type: 'manual',
                                    message: i18next.t(`infos.required`)
                                  });
                                  return false;
                                } else {
                                  clearErrors(`${fieldName}`);
                                  return true;
                                }
                              }}
                              disabled={readOnly}
                            />
                          );

                        default:
                          return (<></>);
                      }
                    })()}
                  </>
                );

              case CalculationResultType.ARITHMETIC_FORMULA_OUTPUT:
                return (
                  <>
                    {
                      (() => {
                        switch (selectedOperator) {
                          case Operator.EQUAL:
                          case Operator.NOT_EQUAL:
                            return !_.isEmpty(availableValueOptions) ? (
                              <RuleSelectInput
                                fieldName={`${fieldName}.${0}`}
                                label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                options={availableValueOptions}
                                isRequired
                                validate={(value) => {
                                  /*TODO*/
                                  return null;
                                }}
                                labelVariant="chip"
                                disabled={readOnly}
                              />
                            ) : (
                              <RuleStandardInput
                                fieldName={`${fieldName}.${0}`}
                                isRequired
                                label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                validate={(value) => {
                                  /*TODO*/
                                  return null;
                                }}
                                disabled={readOnly}
                              />
                            );

                          case Operator.GREATER_THAN:
                          case Operator.GREATER_OR_EQUAL:
                          case Operator.LESS_THAN:
                          case Operator.LESS_OR_EQUAL:
                            return (
                              <RuleStandardInput
                                fieldName={`${fieldName}.${0}`}
                                isRequired
                                label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                validate={(value) => {
                                  /*TODO*/
                                  return null;
                                }}
                                disabled={readOnly}
                              />
                            );

                          case Operator.BETWEEN:
                            return (
                              <>
                                {/*TODO: Two values*/}
                              </>
                            );

                          case Operator.IS_NULL:
                          case Operator.NOT_NULL:
                            return (
                              <>
                                {/*MEANS UNDEFINED*/}
                              </>
                            );

                          case Operator.IN_SET:
                          case Operator.NOT_IN_SET:
                            return !_.isEmpty(availableValueOptions) ? (
                              <RuleSelectInput
                                fieldName={`${fieldName}`}
                                label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                options={availableValueOptions}
                                isRequired
                                validate={(value) => {
                                  /*TODO*/
                                  return null;
                                }}
                                labelVariant="chip"
                                disabled={readOnly}
                              />
                            ) : (
                              <RuleMultipleValueInput
                                fieldName={`${fieldName}`}
                                isRequired
                                label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                validate={(value) => {
                                  /*TODO*/
                                  return null;
                                }}
                                disabled={readOnly}
                              />
                            );

                          default:
                            return (<></>);
                        }
                      })()
                    }
                  </>
                );

              case CalculationResultType.BOOLEAN:
                return (
                  <>
                    {
                      (() => {
                        switch (selectedOperator) {
                          case Operator.EQUAL:
                          case Operator.NOT_EQUAL:
                            return (
                              <RuleSelectInput
                                fieldName={`${fieldName}`}
                                label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                options={[
                                  { value: "true", label: i18next.t("yes") },
                                  { value: "false", label: i18next.t("no") },
                                ]}
                                isRequired
                                validate={() => {
                                  return null;
                                }}
                                labelVariant="chip"
                                disabled={readOnly}
                              />
                            );

                          case Operator.IS_NULL:
                          case Operator.NOT_NULL:
                            return (
                              <>
                                {/*MEANS UNDEFINED*/}
                              </>
                            );

                          default:
                            return (<></>);
                        }
                      })()
                    }
                  </>
                );

              default:
                break;
            }
          })()}
        </>
      );

    case FieldType.SOURCE_DATA:
      return (
        <>
          {(() => {
            switch (selectedFieldSubType) {
              case FieldType.TEXT:
                return (
                  <>
                    {(() => {
                      switch (selectedOperator) {
                        case Operator.EQUAL:
                        case Operator.NOT_EQUAL:
                          return !_.isEmpty(availableValueOptions) ? (
                            <RuleSelectInput
                              fieldName={`${fieldName}.${0}`}
                              label={`${showLabel ? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                              placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                              options={availableValueOptions}
                              isRequired
                              validate={() => {
                                return null;
                              }}
                              uxVariant="criteriaInput"
                              variant="standard"
                              labelVariant="chip"
                              disabled={readOnly}
                            />
                          ) : (
                            <RuleStandardInput
                              fieldName={`${fieldName}.${0}`}
                              isRequired={true}
                              label={`${showLabel ? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                              placeholder={i18next.t(`calculator.dialog.create.placeholder.indicateValue`)}
                              uxVariant="criteriaInput"
                              variant="standard"
                              validate={() => {
                                return null;
                              }}
                              disabled={readOnly}
                            />
                          );

                        case Operator.CONTAINS:
                        case Operator.NOT_CONTAINS:
                        case Operator.STARTS_WITH:
                        case Operator.NOT_STARTS_WITH:
                        case Operator.ENDS_WITH:
                        case Operator.NOT_ENDS_WITH:
                          return (
                            <RuleStandardInput
                              fieldName={`${fieldName}.${0}`}
                              isRequired={true}
                              label={`${showLabel ? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                              placeholder={i18next.t(`calculator.dialog.create.placeholder.indicateValue`)}
                              uxVariant="criteriaInput"
                              variant="standard"
                              validate={() => {
                                return null;
                              }}
                              disabled={readOnly}
                            />
                          );

                        case Operator.IS_NULL:
                        case Operator.NOT_NULL:
                          return (<></>);

                        default:
                          return (<></>);
                      }
                    })()}
                  </>
                );

              case FieldType.NUMERIC:
                return (
                  <>
                    {
                      (() => {
                        switch (selectedOperator) {
                          case Operator.EQUAL:
                          case Operator.NOT_EQUAL:
                            return !_.isEmpty(availableValueOptions) ? (
                              <RuleSelectInput
                                fieldName={`${fieldName}.${0}`}
                                label={`${showLabel ? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                options={availableValueOptions}
                                uxVariant="criteriaInput"
                                variant="standard"
                                isRequired
                                validate={(value) => {
                                  return value && !value.match(REGEXP_DECIMAL)
                                    ? i18next.t("attributes.errors.decimal")
                                    : null;
                                }}
                                labelVariant="chip"
                                disabled={readOnly}
                              />
                            ) : (
                              <RuleStandardInput
                                fieldName={`${fieldName}.${0}`}
                                isRequired
                                uxVariant="criteriaInput"
                                variant="standard"
                                label={`${showLabel ? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                validate={(value) => {
                                  return value && !value.match(REGEXP_DECIMAL)
                                    ? i18next.t("attributes.errors.decimal")
                                    : null;
                                }}
                                disabled={readOnly}
                              />
                            );

                          case Operator.GREATER_THAN:
                          case Operator.GREATER_OR_EQUAL:
                          case Operator.LESS_THAN:
                          case Operator.LESS_OR_EQUAL:
                            return (
                              <RuleStandardInput
                                fieldName={`${fieldName}.${0}`}
                                label={`${showLabel ? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                options={availableValueOptions}
                                uxVariant="criteriaInput"
                                variant="standard"
                                isRequired
                                validate={(value) => {
                                  return value && !value.match(REGEXP_DECIMAL)
                                    ? i18next.t("attributes.errors.decimal")
                                    : null;
                                }}
                                disabled={readOnly}
                              />
                            );

                          case Operator.BETWEEN:
                            return (
                              <>
                                {/*TODO: Two values*/}
                              </>
                            );

                          case Operator.IS_NULL:
                          case Operator.NOT_NULL:
                            return (
                              <>
                                {/*MEANS ATTRIBUTE IS UNDEFINED*/}
                              </>
                            );

                          case Operator.IN_SET:
                          case Operator.NOT_IN_SET:
                            return !_.isEmpty(availableValueOptions) ? (
                              <RuleSelectInput
                                fieldName={`${fieldName}.${0}`}
                                label={`${showLabel ? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                options={availableValueOptions}
                                uxVariant="criteriaInput"
                                variant="standard"
                                isRequired
                                validate={(value) => {
                                  let isValid = true;
                                  if (value && !value.match(REGEXP_DECIMAL)) {
                                    setError(`${fieldName}`, {
                                      type: 'manual',
                                      message: i18next.t("attributes.errors.decimal")
                                    });

                                    isValid = false;
                                  }
                                  return isValid;
                                }}
                                labelVariant="chip"
                                disabled={readOnly}
                              />
                            ) : (
                              <RuleMultipleValueInput
                                fieldName={`${fieldName}`}
                                isRequired
                                label={`${showLabel ? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                                uxVariant="criteriaInput"
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                validate={(value) => {
                                  let isValid = true;
                                  if (value && !value.match(REGEXP_DECIMAL)) {
                                    setError(`${fieldName}`, {
                                      type: 'manual',
                                      message: i18next.t("attributes.errors.decimal")
                                    });

                                    isValid = false;
                                  }


                                  return isValid;
                                }}
                                disabled={readOnly}
                              />
                            );

                          default:
                            return (<></>);
                        }
                      })()
                    }
                  </>
                );

              case FieldType.BOOLEAN:
                return (
                  <>
                    {
                      (() => {
                        switch (selectedOperator) {
                          case Operator.EQUAL:
                          case Operator.NOT_EQUAL:
                            return (
                              <RuleSelectInput
                                fieldName={`${fieldName}.${0}`}
                                label={`${showLabel ? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                                placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                                options={[
                                  { value: "true", label: i18next.t("yes") },
                                  { value: "false", label: i18next.t("no") },
                                ]}
                                isRequired
                                validate={() => {
                                  return null;
                                }}
                                uxVariant="criteriaInput"
                                variant="standard"
                                labelVariant="chip"
                                disabled={readOnly}
                              />
                            );

                          case Operator.IS_NULL:
                          case Operator.NOT_NULL:
                            return (
                              <>
                                {/*MEANS ATTRIBUTE IS UNDEFINED*/}
                              </>
                            );

                          default:
                            return (<></>);
                        }
                      })()
                    }
                  </>
                );

              case FieldType.DATE:
                return (
                  <>
                    {(() => {
                      switch (selectedOperator) {
                        case Operator.EQUAL:
                        case Operator.NOT_EQUAL:
                        case Operator.GREATER_THAN:
                        case Operator.GREATER_OR_EQUAL:
                        case Operator.LESS_THAN:
                        case Operator.LESS_OR_EQUAL:
                          return (
                            <EAInputBloc label={`${showLabel ? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`} customLabelStyle={{ fontSize: "14px" }}>
                              <Controller
                                control={formMethods.control}
                                name={`${fieldName}.${0}`}
                                rules={{
                                  required: i18next.t(`infos.required`),
                                  validate: (value) => {
                                    if (!value) {
                                      return i18next.t(`infos.required`);
                                    }
                                    // Add more custom validation logic if needed
                                    return true;
                                  }
                                }}
                                render={({ field }) => (
                                  <EADatePicker
                                    {...field}
                                    name={field.name}
                                    formMethods={formMethods}
                                    disableHighlightToday
                                    ref={null}
                                    isRequired
                                    disabled={readOnly}
                                  />
                                )}
                              />
                            </EAInputBloc>
                          );


                        case Operator.BETWEEN:
                          return (
                            <>
                              {/*TODO*/}
                            </>
                          );

                        case Operator.IS_NULL:
                        case Operator.NOT_NULL:
                          return (<></>);

                        case Operator.IN_SET:
                        case Operator.NOT_IN_SET:
                          return (
                            <>
                              {/*TODO*/}
                            </>
                          );

                        default:
                          return (<></>);
                      }
                    })()}
                  </>
                );

              default:
                break;
            }
          })()}
        </>
      );

    case FieldType.FORM_FIELD:
    return (
      <>
        {(() => {
          switch (selectedFieldSubType) {
            case DocumentTypePropertyType.CHAINE:
            case DocumentTypePropertyType.MULTILIGNES:
            case DocumentTypePropertyType.EMAIL:
            case DocumentTypePropertyType.TELEPHONE:
            case DocumentTypePropertyType.IBAN:
            case DocumentTypePropertyType.RADIO_BUTTON:
            case DocumentTypePropertyType.LIST_ITEM:
            case DocumentTypePropertyType.BANK_COUNTRY_CODE:
              return (
                <>
                  {(() => {
                    switch (selectedOperator) {
                      case Operator.EQUAL:
                      case Operator.NOT_EQUAL:
                        return !_.isEmpty(availableValueOptions) ? (
                          <RuleSelectInput
                            fieldName={`${fieldName}.${0}`}
                            label={`${showLabel? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                            placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                            options={availableValueOptions}
                            isRequired
                            validate={() => {
                              /*TODO: custom validation if any*/
                              return null;
                            }}
                            variant="standard"
                            labelVariant="chip"
                            disabled={readOnly}
                          />
                        ) : (
                          <RuleStandardInput
                            fieldName={`${fieldName}.${0}`}
                            isRequired={true}
                            label={`${showLabel? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                            placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                            variant="standard"
                            validate={() => {
                              /*TODO: custom validation if any*/
                              return null;
                            }}
                            disabled={readOnly}
                          />
                        );

                      case Operator.CONTAINS:
                      case Operator.NOT_CONTAINS:
                      case Operator.STARTS_WITH:
                      case Operator.NOT_STARTS_WITH:
                      case Operator.ENDS_WITH:
                      case Operator.NOT_ENDS_WITH:
                        return (
                          <RuleStandardInput
                            fieldName={`${fieldName}.${0}`}
                            isRequired={true}
                            label={`${showLabel? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                            placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                            variant="standard"
                            validate={() => {
                              /*TODO: custom validation if any*/
                              return null;
                            }}
                            disabled={readOnly}
                          />
                        );

                      case Operator.IS_NULL:
                      case Operator.NOT_NULL:
                        return (<></>);

                      case Operator.IN_SET:
                      case Operator.NOT_IN_SET:
                        return !_.isEmpty(availableValueOptions) ? (
                          <EAInputBloc label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`} 
                              customLabelStyle={{ fontSize: "14px" }} customChildStyle={{ marginTop: "0px" }}>
                            <EAMultipleSelectInput
                              fieldName={`${fieldName}`}
                              isRequired
                              onChange={(e) => {
                                setValue(`${fieldName}`, e.target.value);
                              }}
                              label={`${showLabel? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                              placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                              options={availableValueOptions}
                              variant="standard"
                              validate={(value) => {
                                if (value.length === 0) {
                                  setError(`${fieldName}`, {
                                    type: 'manual',
                                    message: i18next.t(`infos.required`)
                                  });
                                  return false;
                                } else {
                                  clearErrors(`${fieldName}`);
                                  return true;
                                }
                              }}
                              disabled={readOnly}
                              />
                            </EAInputBloc>
                        ) : (
                          <RuleMultipleValueInput
                            fieldName={`${fieldName}`}
                            isRequired
                            label={`${showLabel? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                            placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                            validate={(value) => {
                              if (value.length === 0) {
                                setError(`${fieldName}`, {
                                  type: 'manual',
                                  message: i18next.t(`infos.required`)
                                });
                                return false;
                              } else {
                                clearErrors(`${fieldName}`);
                                return true;
                              }
                            }}
                            disabled={readOnly}
                          />
                        );

                      default:
                        return (<></>);
                    }
                  })()}
                </>
              );

            case DocumentTypePropertyType.MONTANT:
            case DocumentTypePropertyType.NOMBRE:
            case DocumentTypePropertyType.DEVISE:
              return (
                <>
                  {
                    (() => {
                      const validateValue = (value) => {
                        return value && !value.match(REGEXP_DECIMAL)
                          ? i18next.t("attributes.errors.decimal")
                          : null;
                      }

                      switch (selectedOperator) {
                        case Operator.EQUAL:
                        case Operator.NOT_EQUAL:
                          return !_.isEmpty(availableValueOptions) ? (
                            <RuleSelectInput
                              fieldName={`${fieldName}.${0}`}
                              label={`${showLabel? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                              placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                              options={availableValueOptions}
                              variant="standard"
                              isRequired
                              validate={validateValue}
                              labelVariant="chip"
                              disabled={readOnly}
                            />
                          ) : (
                            <RuleStandardInput
                              fieldName={`${fieldName}.${0}`}
                              isRequired
                              variant="standard"
                              label={`${showLabel? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                              placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                              validate={validateValue}
                              disabled={readOnly}
                            />
                          );

                        case Operator.GREATER_THAN:
                        case Operator.GREATER_OR_EQUAL:
                        case Operator.LESS_THAN:
                        case Operator.LESS_OR_EQUAL:
                          return (
                            <RuleStandardInput
                              fieldName={`${fieldName}.${0}`}
                              isRequired
                              variant="standard"
                              label={`${showLabel? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                              placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                              validate={validateValue}
                              disabled={readOnly}
                            />
                          );

                        case Operator.BETWEEN:
                          return (
                            <>
                              {/*TODO: Two values*/}
                            </>
                          );

                        case Operator.IS_NULL:
                        case Operator.NOT_NULL:
                          return (
                            <>
                              {/*MEANS UNDEFINED*/}
                            </>
                          );

                        case Operator.IN_SET:
                        case Operator.NOT_IN_SET:
                          return !_.isEmpty(availableValueOptions) ? (
                            <RuleSelectInput
                              fieldName={`${fieldName}.${0}`}
                              label={`${showLabel? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                              placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                              options={availableValueOptions}
                              variant="standard"
                              isRequired
                              validate={validateValue}
                              labelVariant="chip"
                              disabled={readOnly}
                            />
                          ) : (
                            <RuleMultipleValueInput
                              fieldName={`${fieldName}`}
                              isRequired
                              label={`${showLabel? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                              placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                              validate={(value) => {
                                let isValid = true;

                                if (value && !value.match(REGEXP_DECIMAL)) {
                                  setError(`${fieldName}`, {
                                    type: 'manual',
                                    message: i18next.t("attributes.errors.decimal")
                                  });

                                  isValid = false;
                                }

                                return isValid;
                              }}
                              disabled={readOnly}
                            />
                          );

                        default:
                          return (<></>);
                      }
                    })()
                  }
                </>
              );

            case DocumentTypePropertyType.DATE:
              return (
                <>
                  {(() => {
                    switch (selectedOperator) {
                      case Operator.EQUAL:
                      case Operator.NOT_EQUAL:
                      case Operator.GREATER_THAN:
                      case Operator.GREATER_OR_EQUAL:
                      case Operator.LESS_THAN:
                      case Operator.LESS_OR_EQUAL:
                        return (
                          <EAInputBloc label={`${showLabel? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`} customLabelStyle={{ fontSize: "14px" }}>
                            <Controller
                              control={formMethods.control}
                              name={`${fieldName}.${0}`}
                              rules={{
                                required: i18next.t(`infos.required`),
                                validate: (value) => {
                                  if (!value) {
                                    return i18next.t(`infos.required`);
                                  }
                                  // Add more custom validation logic if needed
                                  return true;
                                }
                              }}
                              render={({ field }) => (
                                <EADatePicker
                                  name={field.name}
                                  formMethods={formMethods}
                                  disableHighlightToday
                                  isRequired
                                  disabled={readOnly}
                                />
                              )}
                            />
                          </EAInputBloc>
                        );

                      case Operator.BETWEEN:
                        return (
                          <>
                            {/*TODO*/}
                          </>
                        );

                      case Operator.IS_NULL:
                      case Operator.NOT_NULL:
                        return (<></>);

                      case Operator.IN_SET:
                      case Operator.NOT_IN_SET:
                        return (
                          <>
                            {/*TODO*/}
                          </>
                        );

                      default:
                        return (<></>);
                    }
                  })()}
                </>
              );

            case DocumentTypePropertyType.CASE_A_COCHER:
              return (
                <>
                  {(() => {
                    switch (selectedOperator) {
                      case Operator.EQUAL:
                        return !_.isEmpty(availableValueOptions) ? (
                          <EAInputBloc label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`} 
                              customLabelStyle={{ fontSize: "14px" }} customChildStyle={{ marginTop: "0px" }}>
                            <EAMultipleSelectInput
                              fieldName={`${fieldName}`}
                              isRequired
                              onChange={(e) => {
                                setValue(`${fieldName}`, e.target.value);
                              }}
                              label={`${showLabel? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                              placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                              options={availableValueOptions}
                              variant="standard"
                              validate={(value) => {
                                if (value.length === 0) {
                                  setError(`${fieldName}`, {
                                    type: 'manual',
                                    message: i18next.t(`infos.required`)
                                  });
                                  return false;
                                } else {
                                  clearErrors(`${fieldName}`);
                                  return true;
                                }
                              }}
                              disabled={readOnly}
                              />
                            </EAInputBloc>
                        ) : (
                          <RuleMultipleValueInput
                            fieldName={`${fieldName}`}
                            isRequired
                            label={`${showLabel? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                            placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                            validate={(value) => {
                              if (value.length === 0) {
                                setError(`${fieldName}`, {
                                  type: 'manual',
                                  message: i18next.t(`infos.required`)
                                });
                                return false;
                              } else {
                                clearErrors(`${fieldName}`);
                                return true;
                              }
                            }}
                            disabled={readOnly}
                          />
                        );

                      case Operator.CONTAINS:
                      case Operator.NOT_CONTAINS:  
                        return (
                          <EAInputBloc label={showLabel && `${i18next.t("rules.configuration.criteria.fields.value.label")}`} 
                              customLabelStyle={{ fontSize: "14px" }} customChildStyle={{ marginTop: "0px" }}>
                            <EAMultipleSelectInput
                              fieldName={`${fieldName}`}
                              isRequired
                              onChange={(e) => {
                                setValue(`${fieldName}`, e.target.value);
                              }}
                              label={`${showLabel? i18next.t("rules.configuration.criteria.fields.value.label") : ""}`}
                              placeholder={i18next.t(`rules.configuration.criteria.fields.value.placeholder`)}
                              options={availableValueOptions}
                              variant="standard"
                              validate={(value) => {
                                if (value.length === 0) {
                                  setError(`${fieldName}`, {
                                    type: 'manual',
                                    message: i18next.t(`infos.required`)
                                  });
                                  return false;
                                } else {
                                  clearErrors(`${fieldName}`);
                                  return true;
                                }
                              }}
                              disabled={readOnly}
                            />
                          </EAInputBloc>
                        );

                      case Operator.IS_NULL:
                      case Operator.NOT_NULL:
                        return (
                          <>
                            {/*MEANS ATTRIBUTE IS UNDEFINED*/}
                          </>
                        );

                      default:
                        return (<></>);
                    }
                  })()}
                </>
              );

            case DocumentTypePropertyType.LISTE_CHOIX:
              return (
                <>
                  {/*TODO*/}
                </>
              );

            default:
              break;
          }
        })()}
      </>
    );
    default:
      return (
        <></>
      );
  }
}

export default CriterionValues;
